import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {cn} from '../lib/utils';
import {Icons} from '../icons';
import {cva, VariantProps} from 'class-variance-authority';

export const MOBILE_SHEET_EASE_IN = 'ease-sheet-in';
export const MOBILE_SHEET_EASE_OUT = 'ease-sheet-out';

const dialogVariants = cva(
  'fixed z-50 grid w-full p-6 gap-4 shadow-lg duration-200 outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-2xl overflow-y-auto max-h-[80vh]',
  {
    variants: {
      variant: {
        default: 'border bg-popover text-popover-foreground',
        accent: 'bg-accent text-accent-foreground sm:rounded-2xl',
      },
      position: {
        default:
          'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'bottom-left':
          'left-6 bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        'bottom-center':
          'left-[50%] top-[50%] translate-x-[-50%] bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        'bottom-right':
          'right-6 bottom-6 data-[state=closed]:slide-out-to-bottom-16 data-[state=open]:slide-in-from-bottom-16',
        'top-center':
          'left-[50%] translate-x-[-50%] top-[10vh] data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        fullscreen:
          'translate-x-0 translate-y-0 max-h-[unset] inset-5 todesktop:top-12  w-auto data-[state=closed]:slide-out-to-top-16 data-[state=open]:slide-in-from-top-16',
        'mobile-sheet': cn(
          'inset-x-0 top-0 flex h-dvh flex-col gap-0 overflow-hidden rounded-none sm:rounded-none border-0 p-0 shadow-none max-h-none max-w-none bg-background',
          'data-[state=open]:fade-in-100 data-[state=open]:slide-in-from-bottom-full data-[state=open]:duration-300 data-[state=open]:ease-sheet-in',
          'data-[state=closed]:fade-out-100 data-[state=closed]:slide-out-to-bottom-full data-[state=closed]:fill-mode-forwards data-[state=closed]:duration-200 data-[state=closed]:ease-sheet-out',
        ),
      },
      size: {
        default: 'max-w-lg',
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-xl',
        xl: 'max-w-2xl',
        '2xl': 'max-w-3xl',
        fullscreen: 'translate-x-0 translate-y-0 max-h-[unset] inset-5 w-auto',
      },
    },
    defaultVariants: {
      variant: 'default',
      position: 'top-center',
      size: 'default',
    },
  },
);

const dialogCloseVariants = cva(
  'absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default:
          'data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
        accent:
          'w-8 h-8 rounded-lg border border-border-subtle flex items-center justify-center hover:bg-muted transition-colors data-[state=open]:bg-muted data-[state=open]:text-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const Dialog = DialogPrimitive.Root;

export type DialogProps = React.ComponentProps<typeof Dialog>;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const overlayVariants = cva(
  'fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  {
    variants: {
      overlay: {
        default: 'bg-black/80',
        opaque: 'bg-background',
        'mobile-sheet': cn('bg-background duration-300', MOBILE_SHEET_EASE_IN),
      },
    },
    defaultVariants: {
      overlay: 'default',
    },
  },
);

interface DialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
    VariantProps<typeof overlayVariants> {}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({className, overlay, ...props}, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(overlayVariants({overlay}), className)}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogVariants> {
  disablePortal?: boolean;
  hideCloseButton?: boolean;
  overlay?: VariantProps<typeof overlayVariants>['overlay'];
  overlayClassName?: string;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      variant = 'default',
      size = 'default',
      position = 'top-center',
      children,
      disablePortal,
      hideCloseButton = false,
      overlay,
      overlayClassName,
      ...props
    },
    ref,
  ) => {
    const overlayMarkup = (
      <DialogOverlay overlay={overlay} className={overlayClassName} />
    );
    const contentMarkup = (
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogVariants({className, variant, size, position}))}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          document.body.style.pointerEvents = '';
        }}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className={cn(dialogCloseVariants({variant}))}>
            <Icons.x className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    );

    if (disablePortal) {
      return (
        <>
          {overlayMarkup}
          {contentMarkup}
        </>
      );
    }

    return (
      <DialogPortal>
        {overlayMarkup}
        {contentMarkup}
      </DialogPortal>
    );
  },
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-1.5 text-center sm:text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight pb-4',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({className, ...props}, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
